import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { 
  Settings, 
  Search, 
  Filter, 
  Save, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Users,
  ArrowUpDown,
  MoreHorizontal,
  Edit3
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pool and Base options for dropdowns
const POOL_OPTIONS = [
  { value: null, label: "No Pool" },
  { value: "G&A", label: "G&A Pool" },
  { value: "OH", label: "Overhead Pool" },
  { value: "Fringe", label: "Fringe Pool" }
];

const BASE_OPTIONS = [
  { value: "G&A Base", label: "G&A Base" },
  { value: "OH Base", label: "Overhead Base" },
  { value: "Fringe Base", label: "Fringe Base" },
  { value: "Direct Labor", label: "Direct Labor" }
];

const AccountManagement = ({ calculationId, filename, onRatesUpdate }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPool, setFilterPool] = useState("all");
  const [filterAdjusted, setFilterAdjusted] = useState("all");
  const [sortField, setSortField] = useState("account_number");
  const [sortDirection, setSortDirection] = useState("asc");
  
  // Selection and bulk edit state
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkPool, setBulkPool] = useState("");
  const [bulkBases, setBulkBases] = useState([]);
  const [bulkComment, setBulkComment] = useState("");
  
  // Individual edit state
  const [editingAccount, setEditingAccount] = useState(null);
  const [editPool, setEditPool] = useState("");
  const [editBases, setEditBases] = useState([]);
  const [editComment, setEditComment] = useState("");

  useEffect(() => {
    if (calculationId) {
      loadAccountMappings();
    }
  }, [calculationId]);

  const loadAccountMappings = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/calculation/${calculationId}/accounts`);
      setAccounts(response.data.accounts || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Error loading account mappings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAdjustment = async (accountData, newPool, newBases, comment = "") => {
    setSaving(true);
    
    try {
      const adjustmentData = {
        calculation_id: calculationId,
        account_id: accountData.account_id,
        account_number: accountData.account_number,
        account_name: accountData.account_name,
        new_pool: newPool,
        new_bases: newBases,
        user_id: "user", // TODO: Replace with actual user ID
        comment: comment
      };
      
      const response = await axios.post(`${API}/manual-adjustments`, adjustmentData);
      
      if (response.data.success) {
        // Update local account data
        setAccounts(prev => prev.map(acc => 
          acc.account_id === accountData.account_id 
            ? {
                ...acc,
                current_pool: newPool,
                current_bases: newBases,
                has_manual_adjustment: true,
                last_modified: new Date().toISOString()
              }
            : acc
        ));
        
        // Notify parent of rate updates
        if (onRatesUpdate && response.data.updated_rates) {
          onRatesUpdate(response.data.updated_rates, response.data.affected_schedules);
        }
        
        setEditingAccount(null);
        setEditPool("");
        setEditBases([]);
        setEditComment("");
      }
      
    } catch (e) {
      setError(e.response?.data?.detail || "Error saving manual adjustment");
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSave = async () => {
    if (selectedAccounts.length === 0) {
      setError("No accounts selected for bulk update");
      return;
    }
    
    setSaving(true);
    
    try {
      const bulkAdjustments = selectedAccounts.map(accountId => {
        const account = accounts.find(acc => acc.account_id === accountId);
        return {
          account_id: account.account_id,
          account_number: account.account_number,
          account_name: account.account_name,
          new_pool: bulkPool,
          new_bases: bulkBases,
          user_id: "user", // TODO: Replace with actual user ID
          comment: bulkComment
        };
      });
      
      const response = await axios.post(
        `${API}/calculation/${calculationId}/bulk-adjustments`,
        {
          adjustments: bulkAdjustments,
          bulk_comment: bulkComment
        }
      );
      
      if (response.data.success) {
        // Update local account data
        setAccounts(prev => prev.map(acc => 
          selectedAccounts.includes(acc.account_id)
            ? {
                ...acc,
                current_pool: bulkPool,
                current_bases: bulkBases,
                has_manual_adjustment: true,
                last_modified: new Date().toISOString()
              }
            : acc
        ));
        
        // Notify parent of rate updates
        if (onRatesUpdate && response.data.updated_rates) {
          onRatesUpdate(response.data.updated_rates, response.data.affected_schedules);
        }
        
        // Reset bulk edit state
        setSelectedAccounts([]);
        setShowBulkEdit(false);
        setBulkPool("");
        setBulkBases([]);
        setBulkComment("");
      }
      
    } catch (e) {
      setError(e.response?.data?.detail || "Error saving bulk adjustments");
    } finally {
      setSaving(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredAndSortedAccounts = accounts
    .filter(account => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          account.account_number.toLowerCase().includes(searchLower) ||
          account.account_name.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      
      // Pool filter
      if (filterPool !== "all") {
        if (filterPool === "none" && account.current_pool) return false;
        if (filterPool !== "none" && account.current_pool !== filterPool) return false;
      }
      
      // Adjusted filter
      if (filterAdjusted !== "all") {
        if (filterAdjusted === "adjusted" && !account.has_manual_adjustment) return false;
        if (filterAdjusted === "original" && account.has_manual_adjustment) return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === "net_amount") {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = String(aVal || "").toLowerCase();
        bVal = String(bVal || "").toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const formatCurrency = (value) => {
    return value ? `$${value.toLocaleString()}` : 'N/A';
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedAccounts(filteredAndSortedAccounts.map(acc => acc.account_id));
    } else {
      setSelectedAccounts([]);
    }
  };

  const handleSelectAccount = (accountId, checked) => {
    if (checked) {
      setSelectedAccounts(prev => [...prev, accountId]);
    } else {
      setSelectedAccounts(prev => prev.filter(id => id !== accountId));
    }
  };

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Account Classification Management</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Loading account mappings...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Account Classification Management</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-red-700">
              {error}
            </AlertDescription>
          </Alert>
          <Button onClick={loadAccountMappings} className="mt-4">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-blue-600" />
            <span>Account Classification Management</span>
            <Badge variant="secondary">{filename}</Badge>
          </CardTitle>
          
          <div className="flex items-center space-x-2">
            <Badge variant="outline">
              {filteredAndSortedAccounts.length} of {accounts.length} accounts
            </Badge>
            {selectedAccounts.length > 0 && (
              <Button 
                onClick={() => setShowBulkEdit(true)}
                variant="outline"
                size="sm"
              >
                <Users className="h-4 w-4 mr-2" />
                Bulk Edit ({selectedAccounts.length})
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Filters and Search */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search accounts by number or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            
            <Select value={filterPool} onValueChange={setFilterPool}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by Pool" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Pools</SelectItem>
                <SelectItem value="none">No Pool</SelectItem>
                <SelectItem value="G&A">G&A Pool</SelectItem>
                <SelectItem value="OH">Overhead Pool</SelectItem>
                <SelectItem value="Fringe">Fringe Pool</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterAdjusted} onValueChange={setFilterAdjusted}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                <SelectItem value="adjusted">Manual Adjustments</SelectItem>
                <SelectItem value="original">Original Classifications</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator className="mb-6" />

        {/* Accounts Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox 
                    checked={selectedAccounts.length === filteredAndSortedAccounts.length && filteredAndSortedAccounts.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50" 
                  onClick={() => handleSort('account_number')}
                >
                  Account # <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => handleSort('account_name')}
                >
                  Account Name <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                </TableHead>
                <TableHead className="text-right">Net Amount</TableHead>
                <TableHead>Original Pool</TableHead>
                <TableHead>Current Pool</TableHead>
                <TableHead>Current Bases</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedAccounts.map((account) => (
                <TableRow key={account.account_id} className="hover:bg-slate-50">
                  <TableCell>
                    <Checkbox 
                      checked={selectedAccounts.includes(account.account_id)}
                      onCheckedChange={(checked) => handleSelectAccount(account.account_id, checked)}
                    />
                  </TableCell>
                  <TableCell className="font-mono">{account.account_number}</TableCell>
                  <TableCell className="font-medium">{account.account_name}</TableCell>
                  <TableCell className="text-right">{formatCurrency(account.net_amount)}</TableCell>
                  <TableCell>
                    {account.original_pool ? (
                      <Badge variant="outline">{account.original_pool}</Badge>
                    ) : (
                      <span className="text-slate-400">None</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingAccount === account.account_id ? (
                      <Select value={editPool} onValueChange={setEditPool}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Select Pool" />
                        </SelectTrigger>
                        <SelectContent>
                          {POOL_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex items-center space-x-2">
                        {account.current_pool ? (
                          <Badge 
                            variant={account.has_manual_adjustment ? "default" : "outline"}
                            className={account.has_manual_adjustment ? "bg-blue-100 text-blue-800" : ""}
                          >
                            {account.current_pool}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">None</span>
                        )}
                        {account.has_manual_adjustment && (
                          <Badge variant="secondary" className="text-xs">
                            <Edit3 className="h-3 w-3 mr-1" />
                            Modified
                          </Badge>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingAccount === account.account_id ? (
                      <div className="space-y-2">
                        {BASE_OPTIONS.map(option => (
                          <label key={option.value} className="flex items-center space-x-2 text-sm">
                            <Checkbox 
                              checked={editBases.includes(option.value)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setEditBases(prev => [...prev, option.value]);
                                } else {
                                  setEditBases(prev => prev.filter(b => b !== option.value));
                                }
                              }}
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {account.current_bases && account.current_bases.length > 0 ? (
                          account.current_bases.map(base => (
                            <Badge key={base} variant="secondary" className="text-xs">
                              {base}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-slate-400 text-sm">None</span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {account.has_manual_adjustment ? (
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-green-600 text-sm">Adjusted</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-slate-400" />
                        <span className="text-slate-400 text-sm">Original</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingAccount === account.account_id ? (
                      <div className="space-y-2">
                        <Textarea
                          placeholder="Comment (optional)"
                          value={editComment}
                          onChange={(e) => setEditComment(e.target.value)}
                          className="text-sm"
                          rows={2}
                        />
                        <div className="flex space-x-1">
                          <Button 
                            size="sm" 
                            onClick={() => handleSaveAdjustment(account, editPool, editBases, editComment)}
                            disabled={saving}
                          >
                            <Save className="h-3 w-3 mr-1" />
                            Save
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setEditingAccount(null);
                              setEditPool("");
                              setEditBases([]);
                              setEditComment("");
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingAccount(account.account_id);
                          setEditPool(account.current_pool || "");
                          setEditBases(account.current_bases || []);
                          setEditComment("");
                        }}
                      >
                        <Edit3 className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {filteredAndSortedAccounts.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            No accounts found matching the current filters.
          </div>
        )}

        {/* Bulk Edit Modal/Panel */}
        {showBulkEdit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">
                Bulk Edit {selectedAccounts.length} Accounts
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label>New Pool Classification</Label>
                  <Select value={bulkPool} onValueChange={setBulkPool}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Pool" />
                    </SelectTrigger>
                    <SelectContent>
                      {POOL_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>New Base Classifications</Label>
                  <div className="space-y-2 mt-2">
                    {BASE_OPTIONS.map(option => (
                      <label key={option.value} className="flex items-center space-x-2">
                        <Checkbox 
                          checked={bulkBases.includes(option.value)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setBulkBases(prev => [...prev, option.value]);
                            } else {
                              setBulkBases(prev => prev.filter(b => b !== option.value));
                            }
                          }}
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label>Bulk Comment</Label>
                  <Textarea
                    placeholder="Reason for bulk changes..."
                    value={bulkComment}
                    onChange={(e) => setBulkComment(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-2 mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => setShowBulkEdit(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleBulkSave}
                  disabled={saving || !bulkPool}
                >
                  {saving ? "Saving..." : "Save All Changes"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AccountManagement;